import CodeBlock from "@/components/CodeBlock";
import { Card } from "@/components/ui/card";
import { FileCode } from "lucide-react";
import React from "react";

const DomainDoc = () => {
  return (
    <section className="mb-12">
      <h2 className="text-3xl font-bold text-white mb-6">
        <FileCode className="inline h-8 w-8 mr-2 text-emerald-400" />
        Base Entity Classes
      </h2>

      <h3 className="text-2xl font-semibold text-white mb-4 mt-8">
        Entity&lt;T&gt;
      </h3>

      <p className="text-slate-300 mb-4">
        The core abstract entity used across all AxisNet domain models. It
        provides identity, creation and update timestamps, and soft-delete
        support via <code>IsDeleted</code> and
        <code>DeletedAt</code> fields. All domain entities should inherit from
        this class.
      </p>

      <div className="not-prose mb-6">
        <CodeBlock
          language="csharp"
          code={`namespace AxisNet.Domain;

/// <summary>
/// DataAccess entity class for all domain models.
/// Provides common audit fields such as Id, timestamps, and soft delete support.
/// </summary>
public abstract class Entity<T>
{
    /// <summary>
    /// Unique identifier for the entity (e.g., Guid or int).
    /// </summary>
    public T Id { get; set; }

    /// <summary>
    /// The date and time when the record was soft-deleted.
    /// If null, the entity is considered active.
    /// </summary>
    public DateTime? DeletedAt { get; set; }

    /// <summary>
    /// Indicates whether the record is soft-deleted.
    /// True if deleted, otherwise false.
    /// </summary>
    public bool IsDeleted { get; set; } = false;

    /// <summary>
    /// The date and time when the record was created.
    /// </summary>
    public DateTime CreatedAt { get; set; } = DateTime.Now;

    /// <summary>
    /// The date and time when the record was last updated.
    /// </summary>
    public DateTime UpdatedAt { get; set; } = DateTime.Now;
}`}
        />
      </div>

      <h3 className="text-2xl font-semibold text-white mb-4 mt-8">
        Example Usage
      </h3>
      <p className="text-slate-300 mb-4">
        A typical domain entity inheriting from <code>Entity&lt;T&gt;</code>.
        This example demonstrates default audit values and how to initialize the
        entity.
      </p>

      <div className="not-prose mb-6">
        <CodeBlock
          language="csharp"
          code={`public class User : Entity<Guid>
{
    public string FirstName { get; set; }
    public string LastName { get; set; }
    public string Email { get; set; }
    public string PasswordHash { get; set; }
    
    public User(string firstName, string lastName, string email)
    {
        Id = Guid.NewGuid();
        FirstName = firstName;
        LastName = lastName;
        Email = email;

        CreatedAt = DateTime.Now;
        UpdatedAt = DateTime.Now;
    }
}`}
        />
      </div>

      <h3 className="text-xl font-semibold text-white mb-4 mt-8">
        Built-in Properties
      </h3>
      <p className="text-slate-300 mb-4">
        Every entity inheriting from <code>Entity&lt;T&gt;</code> automatically
        includes:
      </p>

      <div className="not-prose mb-6">
        <Card className="p-6 bg-slate-900/50 border-slate-800">
          <ul className="space-y-3 text-slate-300">
            <li>
              <code className="text-emerald-400 font-mono text-sm">Id</code>
              <span className="text-slate-400 ml-2">
                – Strongly typed unique identifier (e.g., Guid or int)
              </span>
            </li>

            <li>
              <code className="text-emerald-400 font-mono text-sm">
                CreatedAt
              </code>
              <span className="text-slate-400 ml-2">
                – Date & time when the entity was created (default:{" "}
                <strong>DateTime.Now</strong>)
              </span>
            </li>

            <li>
              <code className="text-emerald-400 font-mono text-sm">
                UpdatedAt
              </code>
              <span className="text-slate-400 ml-2">
                – Date & time when the entity was last updated (default:{" "}
                <strong>DateTime.Now</strong>)
              </span>
            </li>

            <li>
              <code className="text-emerald-400 font-mono text-sm">
                IsDeleted
              </code>
              <span className="text-slate-400 ml-2">
                – Soft delete flag (true = deleted, false = active)
              </span>
            </li>

            <li>
              <code className="text-emerald-400 font-mono text-sm">
                DeletedAt
              </code>
              <span className="text-slate-400 ml-2">
                – Timestamp of soft deletion (null = not deleted)
              </span>
            </li>
          </ul>
        </Card>
      </div>
    </section>
  );
};

export default DomainDoc;
